import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    OnInit,
    signal,
    viewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { DataViewType } from '@connect-services/client/pages/management/data/deposit/deposit.component';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { ValidatorsService } from '@connect-services/client/services/validators.service';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { Standard, StandardControllerGetRequestParams, StandardsService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { defaultPaginationParameters, paginationMaxTake } from '@shared/client/models/pagination.model';
import { FormService } from '@shared/client/services/form.service';

export class CreateEditDataModalForm extends FormGroup<{
    name: FormControl<string>;
    standard: FormControl<string | null>;
}> {}

export type CreateEditDataModalData = {
    isEditMode: boolean;
    name: string;
    standard?: string | null;
    title: string;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ComplexModalTemplate, RessourcesModule, SelectComponent],
    selector: 'app-create-edit-data-modal',
    templateUrl: './create-edit-data-modal.component.html',
})
export class CreateEditDataModalComponent implements AfterViewInit, OnInit {
    public readonly nameInput = viewChild.required<ElementRef<HTMLInputElement>>('nameInput');
    public readonly standardOptions = signal<SelectOption<string>[]>([]);

    protected createEditDataModalDataForm!: CreateEditDataModalForm;
    protected data: CreateEditDataModalData = inject(MAT_DIALOG_DATA);
    protected dialogRef = inject(MatDialogRef<CreateEditDataModalComponent>);
    protected readonly formService = inject(FormService);

    private readonly fetchAllService = inject(FetchAllService);
    private readonly standardsService = inject(StandardsService);

    public ngAfterViewInit(): void {
        this.nameInput().nativeElement.focus();
    }

    public async ngOnInit(): Promise<void> {
        this.initForm();

        await this.getStandardList();
    }

    public onClickEvent(modalResult: ModalResultEnum): void {
        if (modalResult === ModalResultEnum.CANCEL) {
            this.dialogRef.close(null);

            return;
        }
        this.dialogRef.close(this.createEditDataModalDataForm);
    }

    protected selectStandard(event: string[]): void {
        this.createEditDataModalDataForm.controls.standard.setValue(event[0]);
    }

    private async getStandardList(): Promise<void> {
        const standardList: Standard[] = await this.fetchAllService.getAll<
            StandardControllerGetRequestParams,
            Standard
        >(this.standardsService.standardControllerGet.bind(this.standardsService), {
            ...defaultPaginationParameters,
            take: paginationMaxTake,
            type: DataViewType.ORGANIZATION_DATA,
        });

        this.standardOptions.set(
            standardList.map((standard: Standard) => ({
                label: standard.name,
                value: standard.id,
            })),
        );
    }

    private initForm(): void {
        this.createEditDataModalDataForm = new CreateEditDataModalForm({
            name: new FormControl<string>(this.data.name, {
                nonNullable: true,
                validators: ValidatorsService.stringValidationForm({
                    maxLength: FormService.VARCHAR_COMMON_LENGTH,
                    minLength: FormService.VARCHAR_MIN_LENGTH,
                }),
            }),
            standard: new FormControl<string | null>(this.data.standard ?? null, {
                nonNullable: false,
            }),
        });
    }
}
